// /pack/:packId/practice — answer questions, get sensory feedback, advance.
//
// Step 6 scope: render mcq + image_id, shuffle answer order, fire the four
// "moment #1" cues (glow / scale bounce / confetti / two-tone ding / XP fly)
// on correct, gentle orange + correct highlight + soft descent on wrong.
// Item unlock and streak/XP persistence land in later steps; this page wires
// the visual treatment so the rest of the loop feels right when those land.

import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import {
  isPackId,
  PACKS,
  type CompanionId,
  type OptionId,
  type PackId,
  type PracticeAnswerResponse,
  type PublicOption,
  type PublicQuestion,
  type QueueTargetTopic,
} from '@skena/schema'
import { practiceApi, type PracticeApiError } from '../practice/practiceApi.ts'
import { mulberry32, shuffle } from '../practice/shuffle.ts'
import { fireComboConfetti, fireConfetti } from '../practice/confetti.ts'
import {
  playComboTier,
  playCorrect,
  playEagleSoar,
  playSparrowShield,
  playWrong,
} from '../practice/sounds.ts'
import { XpFly } from '../practice/XpFly.tsx'
import { getDeviceLocalDate } from '../practice/localDate.ts'
import { PROGRESS_QUERY_KEY } from '../practice/progressApi.ts'
import { UnlockOverlay } from '../practice/UnlockOverlay.tsx'
import { INITIAL_COMBO, nextComboState, tierForCombo, type ComboState } from '../practice/combo.ts'
import { TopicFogReveal } from '../practice/TopicFogReveal.tsx'
import {
  CompanionAvatarButton,
  CompanionSelect,
  CompanionSheet,
  EagleSoar,
} from '../practice/CompanionSelect.tsx'
import { CompanionStage, type StageMood } from '../practice/CompanionStage.tsx'
import { CHEERS, GREETINGS, PASSIVE_LINES, pickLine } from '../practice/companionCopy.ts'
import { useMe, useSetCompanion } from '../auth/hooks.ts'

const FEEDBACK_CORRECT_MS = 1100
const FEEDBACK_WRONG_MS = 1700
// 7 lines up with the per-topic question count, so a round is a clean
// "you might just unlock something" beat. Tunable here without touching
// either API or the room-end UI.
const SESSION_LIMIT = 7

const PACK_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-bg)]',
  nativity: 'bg-[var(--color-nativity-bg)]',
}
const PACK_ACCENT_TEXT: Record<PackId, string> = {
  joseph: 'text-[var(--color-joseph-primary)]',
  nativity: 'text-[var(--color-nativity-primary)]',
}
const PACK_ACCENT_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-primary)]',
  nativity: 'bg-[var(--color-nativity-primary)]',
}
// Muted gold pill background for the XP badge — pack-accent in DESIGN.md.
const PACK_GOLD_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-accent)]',
  nativity: 'bg-[var(--color-nativity-accent)]',
}

type Phase =
  | 'loading'
  | 'idle'
  | 'submitting'
  | 'feedback-correct'
  | 'feedback-wrong'
  | 'unlock-overlay'
  | 'done'

interface UnlockedPayload {
  itemId: string
  nameZh: string
}

interface ShuffledQuestion {
  question: PublicQuestion
  options: PublicOption[]
}

export function PracticePage() {
  const { packId: packIdParam } = useParams<{ packId: string }>()
  if (!packIdParam || !isPackId(packIdParam)) {
    return <Navigate to="/" replace />
  }
  return <PracticeInner packId={packIdParam} />
}

function PracticeInner({ packId }: { packId: PackId }) {
  // Bumped each time the kid taps "再來一輪". Two effects: the React Query
  // key changes so the queue refetches (FSRS re-ranks now-reviewed questions
  // to the back), and PracticeRunner remounts (its state resets). One knob,
  // two outcomes — keeps round-handoff logic out of the runner.
  const [roundKey, setRoundKey] = useState(0)

  // Grace companion gate: a user who has never picked sees the full-screen
  // selection before their first question (spec: First-practice selection).
  const meQuery = useMe()
  const setCompanion = useSetCompanion()

  const queueQuery = useQuery({
    queryKey: ['practice-queue', packId, roundKey],
    queryFn: () => practiceApi.getQueue(packId),
    staleTime: 0,
    gcTime: 30 * 60 * 1000,
  })

  // Per-round: keep the server's FSRS-ranked question order (Step 7 puts
  // overdue + brand-new cards first), take the head SESSION_LIMIT, and only
  // shuffle each question's *option* order — spec line 32-33 mandates option
  // randomization on every render. The earlier code re-shuffled the whole
  // queue, which silently undid Step 7's ranking and made unlock-eligible
  // topics very unlikely to surface in any single round.
  const session = useMemo<ShuffledQuestion[] | null>(() => {
    if (!queueQuery.data) return null
    const seed = Math.floor(Math.random() * 1e9)
    const rng = mulberry32(seed)
    const headQuestions = queueQuery.data.questions.slice(0, SESSION_LIMIT)
    return headQuestions.map((question) => ({
      question,
      options: shuffle(question.options, rng),
    }))
  }, [queueQuery.data])

  if (queueQuery.isLoading || meQuery.isLoading || !session) {
    return <FullPageMessage packId={packId} message="準備中…" />
  }
  if (meQuery.data && meQuery.data.companionId === null) {
    return (
      <CompanionSelect
        packId={packId}
        onPick={(id) => setCompanion.mutate(id)}
      />
    )
  }
  if (queueQuery.isError) {
    return (
      <FullPageMessage packId={packId} message="連不到題目，等等再試一次" actionLabel="回主頁" />
    )
  }
  if (session.length === 0) {
    return (
      <FullPageMessage packId={packId} message="這個故事還沒有題目" actionLabel="回主頁" />
    )
  }

  return (
    <PracticeRunner
      key={roundKey}
      packId={packId}
      session={session}
      targetTopic={queueQuery.data?.targetTopic ?? null}
      onPlayAgain={() => setRoundKey((k) => k + 1)}
    />
  )
}

interface XpFlyEvent {
  triggerKey: number
  amount: number
  from: { x: number; y: number }
  to: { x: number; y: number }
}

function PracticeRunner({
  packId,
  session,
  targetTopic,
  onPlayAgain,
}: {
  packId: PackId
  session: ShuffledQuestion[]
  targetTopic: QueueTargetTopic | null
  onPlayAgain: () => void
}) {
  // Round queue is dynamic: wrong answers re-push the question to the tail
  // so the kid retries before the round ends ("Duolingo 補做"). Tracking
  // attempts per question id drives the XP-by-attempt curve.
  const [queue, setQueue] = useState<ShuffledQuestion[]>(session)
  const [cursor, setCursor] = useState(0)
  const [phase, setPhase] = useState<Phase>('idle')
  const [chosenOptionId, setChosenOptionId] = useState<OptionId | null>(null)
  const [correctOptionId, setCorrectOptionId] = useState<OptionId | null>(null)
  const [unlocked, setUnlocked] = useState<UnlockedPayload | null>(null)
  // 常見誤區 for the just-answered question. Non-null only during
  // feedback-wrong; switches that phase from auto-advance to tap-to-continue
  // so the kid reads at their own pace (misconception-field change).
  const [misconception, setMisconception] = useState<string | null>(null)
  const [xpFly, setXpFly] = useState<XpFlyEvent | null>(null)
  // practice-loop-v2: in-round combo (celebration bookkeeping only — never
  // sent to the server; grace principle), companion effect triggers, and
  // the fog-reveal full clear flag for the unlock handoff.
  const [comboState, setComboState] = useState<ComboState>(INITIAL_COMBO)
  const [eagleKey, setEagleKey] = useState<number | null>(null)
  // Companion stage (dogfood round: presence + voice). Bubble auto-clears
  // via timer; mood snaps back to idle on question advance.
  const [stageMood, setStageMood] = useState<StageMood>('idle')
  const [stageBubble, setStageBubble] = useState<string | null>(null)
  const bubbleTimerRef = useRef<number | null>(null)
  const moodTimerRef = useRef<number | null>(null)
  const greetedAsRef = useRef<CompanionId | null>(null)
  // Frozen snapshot of the topic whose last distinct-correct just landed.
  // The answer's onSuccess invalidates the queue, which swaps targetTopic to
  // the NEXT topic mid-feedback — without the freeze, the full-clear brighten
  // would play on (and de-fog) the wrong topic. Cleared when the unlock
  // overlay is dismissed.
  const [fullClearTopic, setFullClearTopic] = useState<QueueTargetTopic | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [companionToast, setCompanionToast] = useState<string | null>(null)
  const [firstTryCorrect, setFirstTryCorrect] = useState(0)
  const [roundXp, setRoundXp] = useState(0)
  // Qids the user got correct *during this round* — drives an optimistic
  // banner update that can't be inferred from the server snapshot alone
  // (server sees only "qid X has at least one correct, ever"). The banner
  // unions this with targetTopic.correctQids to compute live progress.
  const [roundCorrectQids, setRoundCorrectQids] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const attemptsRef = useRef<Map<string, number>>(new Map())
  const xpBadgeRef = useRef<HTMLDivElement | null>(null)
  const questionShownAtRef = useRef<number>(performance.now())

  const meQuery = useMe()
  const setCompanion = useSetCompanion()
  const companionId: CompanionId | null = meQuery.data?.companionId ?? null

  const say = (text: string, ms: number) => {
    if (bubbleTimerRef.current) window.clearTimeout(bubbleTimerRef.current)
    setStageBubble(text)
    bubbleTimerRef.current = window.setTimeout(() => setStageBubble(null), ms)
  }
  const cheerMood = () => {
    if (moodTimerRef.current) window.clearTimeout(moodTimerRef.current)
    setStageMood('cheer')
    moodTimerRef.current = window.setTimeout(() => setStageMood('idle'), 900)
  }

  const navigate = useNavigate()
  const qc = useQueryClient()
  const submitMutation = useMutation<
    PracticeAnswerResponse,
    PracticeApiError,
    {
      questionId: string
      chosenOptionId: OptionId
      timeTakenMs: number
      localDate: string
      attemptIndex: number
    }
  >({
    mutationFn: practiceApi.submitAnswer,
    onSuccess: () => {
      // Streak / XP / item count may have changed — invalidate so HomePage
      // and any other progress reader refetches.
      qc.invalidateQueries({ queryKey: PROGRESS_QUERY_KEY })
      // Re-pull the queue so the topic banner's distinctCorrect count
      // refreshes mid-round. The runner's internal `queue` state is
      // initialised once via useState, so refetched questions don't
      // disturb the round in flight.
      qc.invalidateQueries({ queryKey: ['practice-queue', packId] })
    },
  })

  // Cursor-driven phase reset. Also detects round end (cursor walked off the
  // queue, including any retry pushes).
  useEffect(() => {
    if (cursor >= queue.length) {
      setPhase('done')
      return
    }
    questionShownAtRef.current = performance.now()
    setPhase('idle')
    setChosenOptionId(null)
    setCorrectOptionId(null)
    setMisconception(null)
    setEagleKey(null)
    setStageMood('idle')
  }, [cursor, queue.length])

  // C. 開場招呼 — greet once per companion (round start + after a switch).
  useEffect(() => {
    if (!companionId || greetedAsRef.current === companionId) return
    greetedAsRef.current = companionId
    say(pickLine(GREETINGS[companionId]), 2600)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companionId])

  const onPickOption = async (option: PublicOption, ev: MouseEvent<HTMLButtonElement>) => {
    if (phase !== 'idle') return
    const current = queue[cursor]
    if (!current) return
    const qid = current.question.id
    const attemptIndex = attemptsRef.current.get(qid) ?? 0

    setPhase('submitting')
    setChosenOptionId(option.id)
    const timeTakenMs = Math.min(10 * 60 * 1000, Math.round(performance.now() - questionShownAtRef.current))
    const rect = ev.currentTarget.getBoundingClientRect()
    const clickPoint = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }

    try {
      const result = await submitMutation.mutateAsync({
        questionId: qid,
        chosenOptionId: option.id,
        timeTakenMs,
        localDate: getDeviceLocalDate(),
        attemptIndex,
      })
      setCorrectOptionId(result.correctOptionId)
      const justUnlocked: UnlockedPayload | null =
        result.unlockedItemId && result.unlockedItemNameZh
          ? { itemId: result.unlockedItemId, nameZh: result.unlockedItemNameZh }
          : null
      setUnlocked(justUnlocked)
      setRoundXp((x) => x + result.xpDelta)

      if (result.isCorrect) {
        if (attemptIndex === 0) setFirstTryCorrect((c) => c + 1)
        setRoundCorrectQids((prev) => {
          if (prev.has(qid)) return prev
          const next = new Set(prev)
          next.add(qid)
          return next
        })
        setPhase('feedback-correct')
        playCorrect()
        const originY = clickPoint.y / window.innerHeight
        const originX = clickPoint.x / window.innerWidth
        fireConfetti(packId, originX, originY)
        // Combo bookkeeping + tier celebration (celebration-only; no XP).
        const advanced = nextComboState(comboState, { isCorrect: true, companionId })
        setComboState(advanced.state)
        const tier = tierForCombo(advanced.state.combo)
        if (tier !== 0) {
          fireComboConfetti(packId, tier, companionId === 'donkey', originX, originY)
          playComboTier(tier, companionId === 'donkey')
        }
        // Eagle passive: a retried question answered right soars.
        const eagleSoared = companionId === 'eagle' && attemptIndex > 0
        if (eagleSoared) {
          setEagleKey(Date.now())
          playEagleSoar()
        }
        // Companion voice: passive attribution wins over the plain cheer
        // (B before D), and the stage always jumps.
        if (companionId) {
          cheerMood()
          if (eagleSoared) {
            say(PASSIVE_LINES.eagle, 1800)
          } else if (companionId === 'donkey' && tier !== 0) {
            say(PASSIVE_LINES.donkey, 1800)
          } else {
            say(pickLine(CHEERS[companionId]), 1100)
          }
        }
        // Fog-reveal full clear: the unlock firing means this was the topic's
        // last distinct-correct — freeze the banner on this topic and play
        // the whole-image brighten before the unlock overlay takes over.
        if (justUnlocked && targetTopic) setFullClearTopic(targetTopic)
        const badge = xpBadgeRef.current?.getBoundingClientRect()
        if (badge && result.xpDelta > 0) {
          setXpFly({
            triggerKey: Date.now(),
            amount: result.xpDelta,
            from: clickPoint,
            to: { x: badge.left + badge.width / 2, y: badge.top + badge.height / 2 },
          })
        }
        if (justUnlocked) {
          // Hold on the correct-feedback for a beat so confetti + glow read,
          // then surface the unlock overlay. Spec says "don't immediately
          // advance" — the overlay drives navigation from here.
          setTimeout(() => setPhase('unlock-overlay'), FEEDBACK_CORRECT_MS)
        } else {
          setTimeout(() => setCursor((c) => c + 1), FEEDBACK_CORRECT_MS)
        }
      } else {
        // Push the question back to the tail of the round queue so the kid
        // gets to retry it before the round ends. Increment attempt count
        // so the next submission lands on the XP decay curve.
        attemptsRef.current.set(qid, attemptIndex + 1)
        setPhase('feedback-wrong')
        playWrong()
        // Combo: sparrow's once-per-round shield keeps it; otherwise a
        // silent reset (no loss messaging — spec: quiet loss).
        const dropped = nextComboState(comboState, { isCorrect: false, companionId })
        setComboState(dropped.state)
        if (companionId) setStageMood('comfort')
        if (dropped.event === 'shielded') {
          playSparrowShield()
          say(PASSIVE_LINES.sparrow, 3000)
        }
        if (result.misconceptionZh) {
          // Misconception card takes over pacing: no auto-advance, the kid
          // taps 「知道了」 when done reading (onMisconceptionAck).
          setMisconception(result.misconceptionZh)
        } else {
          setTimeout(() => {
            setQueue((q) => [...q, current])
            setCursor((c) => c + 1)
          }, FEEDBACK_WRONG_MS)
        }
      }
    } catch {
      // Network or validation error — let the kid try again on this question.
      setPhase('idle')
      setChosenOptionId(null)
    }
  }

  if (phase === 'done') {
    return (
      <RoundDonePage
        packId={packId}
        firstTryCorrect={firstTryCorrect}
        totalAnswered={queue.length}
        roundXp={roundXp}
        targetTopic={targetTopic}
        onPlayAgain={onPlayAgain}
      />
    )
  }

  const current = queue[cursor]!

  return (
    <main
      className={`relative mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-6 pb-28 ${PACK_BG[packId]}`}
      data-pack={packId}
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to={`/pack/${packId}`}
            className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
            aria-label="回 pack 主頁"
          >
            ← 退出
          </Link>
          {companionId ? (
            <CompanionAvatarButton companionId={companionId} onOpen={() => setSheetOpen(true)} />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {comboState.combo >= 2 ? (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="rounded-[var(--radius-pill)] bg-white/80 px-2.5 py-1 text-xs font-bold text-[var(--color-fg)] shadow-sm"
                aria-label={`${comboState.combo} 連對`}
              >
                🔥 {comboState.combo} 連對
              </motion.span>
            ) : null}
          </AnimatePresence>
          <div
            ref={xpBadgeRef}
            className={`rounded-[var(--radius-pill)] px-3 py-1 text-xs font-bold text-[var(--color-fg)] shadow-sm ${PACK_GOLD_BG[packId]}`}
            aria-label="XP 暫存"
          >
            {roundXp} XP
          </div>
        </div>
      </header>
      {fullClearTopic || targetTopic ? (
        <TopicFogReveal
          packId={packId}
          targetTopic={(fullClearTopic ?? targetTopic)!}
          roundCorrectQids={roundCorrectQids}
          topicQueueQids={queue
            .filter((s) => s.question.topicId === (fullClearTopic ?? targetTopic)!.topicId)
            .map((s) => s.question.id)}
          fullClear={fullClearTopic !== null}
        />
      ) : (
        <TopicBanner
          packId={packId}
          targetTopic={targetTopic}
          roundCorrectQids={roundCorrectQids}
        />
      )}

      <AnimatePresence mode="wait" initial={false}>
        <QuestionCard
          key={current.question.id}
          packId={packId}
          shuffled={current}
          phase={phase}
          chosenOptionId={chosenOptionId}
          correctOptionId={correctOptionId}
          onPick={onPickOption}
        />
      </AnimatePresence>

      {phase === 'feedback-wrong' && misconception ? (
        <MisconceptionCard
          text={misconception}
          onAck={() => {
            const cur = queue[cursor]
            setMisconception(null)
            if (cur) setQueue((q) => [...q, cur])
            setCursor((c) => c + 1)
          }}
        />
      ) : null}

      {xpFly ? (
        <XpFly
          triggerKey={xpFly.triggerKey}
          amount={xpFly.amount}
          packId={packId}
          from={xpFly.from}
          to={xpFly.to}
          onDone={() => setXpFly(null)}
        />
      ) : null}

      <CompanionSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onPick={(id) => {
          setSheetOpen(false)
          setCompanion.mutate(id, {
            onError: () => {
              setCompanionToast('下次連線再幫你記住')
              setTimeout(() => setCompanionToast(null), 2500)
            },
          })
        }}
      />
      {companionId ? (
        <CompanionStage companionId={companionId} mood={stageMood} bubble={stageBubble} />
      ) : null}
      <AnimatePresence>
        {eagleKey !== null ? <EagleSoar triggerKey={eagleKey} /> : null}
      </AnimatePresence>
      <AnimatePresence>
        {companionToast ? (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none fixed top-6 left-1/2 z-50 -translate-x-1/2 rounded-[var(--radius-pill)] bg-[var(--color-fg)] px-4 py-2 text-sm text-white shadow"
            role="status"
          >
            {companionToast}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {phase === 'unlock-overlay' && unlocked ? (
        <UnlockOverlay
          packId={packId}
          itemId={unlocked.itemId}
          nameZh={unlocked.nameZh}
          onColorNow={() => navigate(`/pack/${packId}/color/${unlocked.itemId}`)}
          onLater={() => {
            setUnlocked(null)
            setFullClearTopic(null)
            // If this was the last question of the round, jump straight
            // to done. Otherwise the cursor effect needs a render frame
            // to flip phase, leaving an empty render in between (cursor
            // walks past queue.length while phase is still 'unlock-overlay',
            // and queue[cursor] is undefined → blank screen).
            if (cursor + 1 >= queue.length) {
              setPhase('done')
            } else {
              setCursor((c) => c + 1)
            }
          }}
        />
      ) : null}
    </main>
  )
}

function MisconceptionCard({ text, onAck }: { text: string; onAck: () => void }) {
  // Gentle orange family (same palette as the wrong-option state — never
  // red). role="status" + polite live region so screen readers announce the
  // text without stealing focus mid-answer.
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      role="status"
      aria-live="polite"
      className="flex flex-col gap-3 rounded-[var(--radius-soft)] border-2 border-orange-200 bg-orange-50 p-4"
    >
      <span className="self-start rounded-[var(--radius-pill)] bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700">
        常見誤區
      </span>
      <p className="text-base leading-relaxed text-[var(--color-fg)]">{text}</p>
      <button
        type="button"
        autoFocus
        onClick={onAck}
        className="min-h-12 w-full rounded-[var(--radius-soft)] bg-orange-400 text-base font-bold text-white transition-transform hover:bg-orange-500 active:scale-[0.98]"
      >
        知道了
      </button>
    </motion.section>
  )
}

interface QuestionCardProps {
  packId: PackId
  shuffled: ShuffledQuestion
  phase: Phase
  chosenOptionId: OptionId | null
  correctOptionId: OptionId | null
  onPick: (option: PublicOption, ev: MouseEvent<HTMLButtonElement>) => void
}

const cardVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.09, delayChildren: 0.05 },
  },
  exit: { opacity: 0, y: -8, transition: { duration: 0.18 } },
}

const childVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
}

function QuestionCard({
  packId,
  shuffled,
  phase,
  chosenOptionId,
  correctOptionId,
  onPick,
}: QuestionCardProps) {
  const { question, options } = shuffled
  const locked = phase !== 'idle'
  return (
    <motion.section
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="flex flex-col gap-4"
    >
      {question.type === 'image_id' && question.imageUrl ? (
        <motion.div variants={childVariants}>
          <ImageStem url={question.imageUrl} alt={question.promptZh} />
        </motion.div>
      ) : null}

      <motion.div
        variants={childVariants}
        className="skena-frame relative flex flex-col gap-2 rounded-[var(--radius-soft)] border border-[var(--color-decoration)]/30 bg-white/70 p-5 shadow-[var(--shadow-card)]"
      >
        <p className="relative flex items-baseline gap-1.5 text-xs text-[var(--color-muted)]">
          <span aria-hidden="true" className="text-[var(--color-decoration)]">❦</span>
          <span className="tracking-wider uppercase">{question.scriptureRef}</span>
        </p>
        <h2 className="relative font-[family-name:var(--font-display)] text-xl font-bold text-[var(--color-fg)]">
          {question.promptZh}
        </h2>
      </motion.div>

      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const state = optionState({ option, phase, chosenOptionId, correctOptionId })
          return (
            <motion.div key={option.id} variants={childVariants}>
              <OptionButton
                packId={packId}
                option={option}
                state={state}
                disabled={locked}
                onPick={onPick}
              />
            </motion.div>
          )
        })}
      </div>
    </motion.section>
  )
}

type OptionState = 'idle' | 'pending' | 'correct' | 'wrong' | 'reveal-correct' | 'dim'

function optionState(args: {
  option: PublicOption
  phase: Phase
  chosenOptionId: OptionId | null
  correctOptionId: OptionId | null
}): OptionState {
  const { option, phase, chosenOptionId, correctOptionId } = args
  if (phase === 'idle') return 'idle'
  if (phase === 'submitting') return option.id === chosenOptionId ? 'pending' : 'dim'
  if (phase === 'feedback-correct') {
    return option.id === chosenOptionId ? 'correct' : 'dim'
  }
  if (phase === 'feedback-wrong') {
    if (option.id === chosenOptionId) return 'wrong'
    if (option.id === correctOptionId) return 'reveal-correct'
    return 'dim'
  }
  return 'idle'
}

function OptionButton({
  packId,
  option,
  state,
  disabled,
  onPick,
}: {
  packId: PackId
  option: PublicOption
  state: OptionState
  disabled: boolean
  onPick: (option: PublicOption, ev: MouseEvent<HTMLButtonElement>) => void
}) {
  // Color rules:
  //   correct        — green ring + bg tint, scale-bounce
  //   wrong          — orange (NOT red — non-punitive)
  //   reveal-correct — green outline highlights what the right answer was
  //   pending        — pack-accent ring + slight shrink
  //   dim            — fade other options so attention stays on the choice
  const styles = optionStyles(packId, state)
  const animate =
    state === 'correct'
      ? { scale: [1, 1.04, 1] }
      : state === 'wrong'
        ? { x: [0, -4, 4, -3, 3, 0] }
        : { scale: 1, x: 0 }
  return (
    <motion.button
      type="button"
      onClick={(ev) => onPick(option, ev)}
      disabled={disabled}
      whileTap={disabled ? {} : { scale: 0.98 }}
      animate={animate}
      transition={{ duration: state === 'wrong' ? 0.4 : 0.45, ease: 'easeOut' }}
      className={`w-full rounded-[var(--radius-soft)] border-2 px-5 py-4 text-left text-base text-[var(--color-fg)] transition-colors ${styles}`}
    >
      {option.textZh}
    </motion.button>
  )
}

function optionStyles(packId: PackId, state: OptionState): string {
  switch (state) {
    case 'idle':
      return 'border-black/10 bg-white hover:border-current/30 hover:-translate-y-0.5 active:translate-y-0'
    case 'pending':
      return packId === 'joseph'
        ? 'border-[var(--color-joseph-primary)] bg-white opacity-90'
        : 'border-[var(--color-nativity-primary)] bg-white opacity-90'
    case 'correct':
      return 'border-emerald-500 bg-emerald-50 shadow-[0_0_0_4px_rgba(16,185,129,0.15)]'
    case 'wrong':
      return 'border-orange-400 bg-orange-50'
    case 'reveal-correct':
      return 'border-emerald-500 bg-emerald-50/60'
    case 'dim':
      return 'border-black/5 bg-white/60 opacity-60'
  }
}

function ImageStem({ url, alt }: { url: string; alt: string }) {
  const [loaded, setLoaded] = useState(false)
  const [errored, setErrored] = useState(false)
  return (
    <div className="relative w-full overflow-hidden rounded-[var(--radius-soft)] bg-black/5 shadow-sm">
      <div
        className="w-full"
        style={{ aspectRatio: '1408 / 786' }}
        aria-hidden={!loaded}
      />
      <AnimatePresence>
        {!loaded && !errored ? (
          <motion.div
            key="ph"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 animate-pulse bg-gradient-to-br from-black/5 to-black/10"
            aria-hidden
          />
        ) : null}
      </AnimatePresence>
      {errored ? (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-[var(--color-muted)]">
          圖片還沒準備好
        </div>
      ) : (
        <motion.img
          src={url}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
    </div>
  )
}

function RoundDonePage({
  packId,
  firstTryCorrect,
  totalAnswered,
  roundXp,
  targetTopic,
  onPlayAgain,
}: {
  packId: PackId
  /** Questions the kid got right on the first try this round. */
  firstTryCorrect: number
  /** Every question shown — original 7 + retries pushed to the tail. */
  totalAnswered: number
  /** XP earned this round (server-driven, attempt-aware). */
  roundXp: number
  /** Target topic this round attacked, if any. */
  targetTopic: QueueTargetTopic | null
  onPlayAgain: () => void
}) {
  // The pre-retry baseline is targetTopic.total (typically 7) — that's the
  // "ideal" round size before any wrong-answer pushes. Fall back to
  // totalAnswered (which equals the original size when no retries happened).
  const baseTotal = targetTopic?.total ?? totalAnswered
  const firstTryPct = baseTotal > 0 ? firstTryCorrect / baseTotal : 0
  const stars = firstTryPct >= 0.85 ? 3 : firstTryPct >= 0.6 ? 2 : 1
  const perfect = stars === 3 && firstTryCorrect === baseTotal

  useEffect(() => {
    if (perfect) {
      fireConfetti(packId, 0.5, 0.45)
      const t = setTimeout(() => fireConfetti(packId, 0.5, 0.45), 220)
      return () => clearTimeout(t)
    }
    return
  }, [perfect, packId])

  const headline = perfect
    ? '一次過！太棒了'
    : stars === 2
      ? '完成這一輪'
      : '撐到了，繼續就好'
  const subline = `一次答對 ${firstTryCorrect} / ${baseTotal} · +${roundXp} XP`
  const encourage =
    stars === 3
      ? '掌握得很好，再來一輪試試別的故事'
      : stars === 2
        ? '再多練一輪會更熟'
        : '別灰心，多練幾次就熟了'

  return (
    <main
      className={`mx-auto flex min-h-full max-w-md flex-col items-center gap-6 p-8 text-center ${PACK_BG[packId]}`}
    >
      <StarsRow stars={stars} />
      <div className="flex flex-col items-center gap-2">
        <h1 className="font-[family-name:var(--font-display)] text-3xl font-black text-[var(--color-fg)]">
          <span aria-hidden="true" className="text-[var(--color-decoration)]">✦</span>
          {' '}{headline}{' '}
          <span aria-hidden="true" className="text-[var(--color-decoration)]">✦</span>
        </h1>
        <p className={`text-base ${PACK_ACCENT_TEXT[packId]} font-semibold`}>{subline}</p>
        <p className="text-sm text-[var(--color-muted)]">{encourage}</p>
      </div>

      <div className="flex w-full flex-col gap-3">
        <button
          type="button"
          onClick={onPlayAgain}
          className={`rounded-[var(--radius-pill)] px-6 py-4 text-base font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5 hover:shadow-lg ${PACK_ACCENT_BG[packId]}`}
        >
          再來一輪
        </button>
        <Link
          to={`/pack/${packId}`}
          className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-6 py-3 text-sm text-[var(--color-muted)] hover:border-[var(--color-muted)]/60"
        >
          回 {PACKS[packId].nameZh}
        </Link>
      </div>
    </main>
  )
}

function StarsRow({ stars }: { stars: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center gap-2" role="img" aria-label={`${stars} 顆星`}>
      {[1, 2, 3].map((idx) => (
        <motion.span
          key={idx}
          className={`text-4xl ${idx <= stars ? '' : 'opacity-25 grayscale'}`}
          initial={{ scale: 0, rotate: -25 }}
          animate={{ scale: idx <= stars ? 1 : 0.85, rotate: 0 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 18,
            delay: idx * 0.12,
          }}
          aria-hidden="true"
        >
          ⭐
        </motion.span>
      ))}
    </div>
  )
}

function TopicBanner({
  packId,
  targetTopic,
  roundCorrectQids,
}: {
  packId: PackId
  targetTopic: QueueTargetTopic | null
  roundCorrectQids: ReadonlySet<string>
}) {
  if (!targetTopic) {
    return (
      <div className="skena-frame relative rounded-[var(--radius-soft)] border border-[var(--color-decoration)]/30 bg-white/60 px-4 py-3 text-sm text-[var(--color-muted)] shadow-[var(--shadow-card)]">
        <p className="relative">複習中 · 這個故事都解鎖啦</p>
      </div>
    )
  }
  // Live distinct = (server's already-correct qids) ∪ (qids the user just
  // landed correctly this round, which the server snapshot can't see yet).
  // The qid sets are disjoint by construction unless the server already had
  // them — Set union dedups regardless.
  const live = new Set<string>(targetTopic.correctQids)
  for (const qid of roundCorrectQids) live.add(qid)
  const liveDistinct = Math.min(targetTopic.total, live.size)
  const pct = targetTopic.total > 0 ? liveDistinct / targetTopic.total : 0
  return (
    <div className="skena-frame relative rounded-[var(--radius-soft)] border border-[var(--color-decoration)]/30 bg-white/60 px-4 py-3 shadow-[var(--shadow-card)]">
      <div className="relative flex items-baseline justify-between text-sm">
        <span className="text-[var(--color-fg)]">
          <span className="text-[var(--color-muted)]">攻略中：</span>
          <span aria-hidden="true" className="text-[var(--color-decoration)]">✦</span>
          {' '}
          <span className="font-semibold">{targetTopic.nameZh}</span>
        </span>
        <motion.span
          key={liveDistinct}
          className="text-xs tabular-nums text-[var(--color-muted)]"
          initial={{ scale: 1.25, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 380, damping: 24 }}
        >
          {liveDistinct}/{targetTopic.total} ✓
        </motion.span>
      </div>
      <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-decoration)]/15">
        <motion.div
          className={`h-full ${PACK_ACCENT_BG[packId]}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct * 100}%` }}
          transition={{ type: 'spring', stiffness: 220, damping: 28 }}
        />
      </div>
    </div>
  )
}

function FullPageMessage({
  packId,
  message,
  actionLabel,
}: {
  packId: PackId
  message: string
  actionLabel?: string
}) {
  return (
    <main className={`mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-4 p-8 text-center ${PACK_BG[packId]}`}>
      <p className="text-[var(--color-muted)]">{message}</p>
      {actionLabel ? (
        <Link
          to="/"
          className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-5 py-2 text-sm"
        >
          {actionLabel}
        </Link>
      ) : null}
    </main>
  )
}
